<template>
  <span
    ref="rootRef"
    contenteditable="false"
    :class="componentClass"
    :data-anchor="anchorLocal"
    :data-link-type="dataLinkTypeLocal"
    :data-label="customTextLocal"
    :data-valid="isValid"
    @click="handleClick"
  >
    <a class="link">
      <span v-if="isValid && !hasCustomText" class="icon-wrapper">
        <svg-icon :name="icon" kind="indicative"></svg-icon>
        <span
          :class="{
            'edit-mode': isEditMode,
            'hide-section-activity-number':
              isDestinationSection &&
              dynamicLinkIcon.dynamicLinkIcon_hideSectionActivityNumber.value,
            'letter-icon-label': isDestinationSection,
            'number-icon-label': !isDestinationSection,
          }"
          :data-icon-text="activityIconText"
        />
      </span>
      <span class="label" :data-anchor="anchorLocal" :data-text="label"></span>
      <div v-if="!isNotebookAnchor && isPageType && isValid && !hasCustomText" class="icon-wrapper">
        <svg-icon name="Pagination32" kind="indicative"></svg-icon>
        <span class="number-icon-label" :data-icon-text="pageIconText" />
      </div>
    </a>
  </span>
</template>

<script setup lang="ts">
import { ANCHOR_LESSON_COVER, CLASS, LINK_TYPE } from "@plugins/dynamic-link/dynamic.link.constant";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import { useDynamicLinkIcon } from "@plugins/dynamic-link/composables/dynamic-link-icon.ts";
import { useDynamicMountChild } from "@plugins/common/composables/dynamic-mount-child.ts";
import { isEmpty } from "@utils/var.check";
import { computed, onMounted, ref, watch } from "vue";
import { useDynamicLinkStore } from "@plugins/dynamic-link/dynamic.link.store.ts";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { useStore } from "vuex";
import { NAMESPACE } from "@/modules/attempt/service/attempt.constant";

defineOptions({
  name: "DynamicInternalLinkView",
});

const props = defineProps({
  // in edit mode, reachability of the destination does not consider if the covers are shown.
  isEditMode: {
    type: Boolean,
    required: true,
  },
  anchor: {
    type: String,
    required: true,
  },
  customText: {
    type: String,
    required: false,
    default: "",
  },
  dataLinkType: {
    type: String,
    required: false,
    default: "",
  },
  introductionTitle: {
    type: String,
    required: true,
  },
  lessonUuid: {
    type: String,
    required: false,
    default: "",
  },
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_parentComponent: { type: Object, default: null },
  // eslint-disable-next-line vue/prop-name-casing
  dynamicMountChildMixin_isDynamicallyMounted: { type: Boolean, default: false },
});

const emit = defineEmits(["click"]);
const rootRef = ref<HTMLElement | null>(null);

const defaultGenericText = "Link";
const componentClass = [CLASS.DYNAMIC_LINK, CLASS.COMPONENT];
const customTextLocal = ref(props.customText);
const dataLinkTypeLocal = ref(props.dataLinkType);
const anchorLocal = ref(props.anchor);

const store = useStore();
const dynamicLinkStore = useDynamicLinkStore();
const dynamicLinkIcon = useDynamicLinkIcon();
useDynamicMountChild({
  dynamicMountChildMixin_parentComponent: props.dynamicMountChildMixin_parentComponent,
  dynamicMountChildMixin_isDynamicallyMounted: props.dynamicMountChildMixin_isDynamicallyMounted,
});

const getLesson = computed(() => store.getters[`${NAMESPACE.RESOURCE}/getLesson`]);

const isDestinationIntroduction = computed(() => anchorLocal.value === ANCHOR_LESSON_COVER);

const currentPage = computed(() => {
  if (dataLinkTypeLocal.value === LINK_TYPE.PAGE) {
    return (
      dynamicLinkStore.getAnchorPage(anchorLocal.value) ??
      dynamicLinkStore.getAnchorNotebookPage(anchorLocal.value) ??
      null
    );
  }
  return null;
});

const isNotebookAnchor = computed(() => {
  return !isEmpty(dynamicLinkStore.getAnchorNotebookPage(anchorLocal.value));
});

const currentActivity = computed(() => {
  if (isDestinationIntroduction.value) {
    return null;
  }
  if (isEmpty(anchorLocal.value)) {
    return null;
  }
  if (dataLinkTypeLocal.value === LINK_TYPE.PAGE) {
    return dynamicLinkStore.getActivityByAnchorPage(anchorLocal.value) ?? null;
  }
  return dynamicLinkStore.getAnchorActivity(anchorLocal.value) ?? null;
});

const isDestinationSection = computed(() => {
  return (
    dataLinkTypeLocal.value === LINK_TYPE.ACTIVITY &&
    currentActivity.value?.type === LESSON_ACTIVITY_TYPE.SECTION
  );
});

const isValid = computed(() => {
  const isDestinationFound =
    !!currentActivity.value ||
    isDestinationIntroduction.value ||
    (isPageType.value && !!currentPage.value);

  if (props.isEditMode) {
    return isDestinationFound;
  } else {
    const isDestinationHidden =
      (isDestinationSection.value && !dynamicLinkStore.getShowSectionCover) ||
      (isDestinationIntroduction.value && !dynamicLinkStore.getShowCover);
    return isDestinationFound && !isDestinationHidden;
  }
});

const hasCustomText = computed(() => {
  return customTextLocal.value && customTextLocal.value.length;
});

const isPageType = computed(() => {
  return (
    dataLinkTypeLocal.value === LINK_TYPE.PAGE &&
    (isNotebookAnchor.value || (currentActivity.value && currentActivity?.value?.pageCount > 1))
  );
});

const label = computed(() => {
  if (!isValid.value) {
    return defaultGenericText;
  }

  if (hasCustomText.value) {
    return customTextLocal.value;
  }

  if (isNotebookAnchor.value) {
    return currentPage.value?.title;
  }

  let label;
  if (isDestinationIntroduction.value) {
    label = props.introductionTitle;
  } else {
    label = currentActivity.value?.title;
  }
  return isPageType.value ? `${label} :` : label;
});

const icon = computed(() => {
  if (!isValid.value) {
    return "";
  }
  if (isNotebookAnchor.value) {
    // notebook type
    return dynamicLinkIcon.dynamicLinkIcon_pageIcon;
  }

  if (isDestinationIntroduction.value) {
    return dynamicLinkIcon.dynamicLinkIcon_introductionIcon.value;
  }
  if (isDestinationSection.value) {
    return dynamicLinkIcon.dynamicLinkIcon_sectionIcon.value;
  } else {
    return dynamicLinkIcon.dynamicLinkIcon_activityIcon(
      currentActivity.value?.type,
      currentActivity.value?.optional,
    );
  }
});

const activityIconText = computed(() => {
  if (!isValid.value || isDestinationIntroduction.value) {
    return "";
  }

  if (isNotebookAnchor.value) {
    // notebook page icon number
    return currentPage.value?.indexNo + 1;
  }

  if (isDestinationSection.value) {
    return dynamicLinkIcon.dynamicLinkIcon_parseSectionIconLetter(currentActivity.value?.pageNo);
  } else {
    return currentActivity.value?.pageNo;
  }
});

const pageIconText = computed(() => {
  // activity page icon number
  if (dataLinkTypeLocal.value === LINK_TYPE.PAGE) {
    return currentPage.value?.indexNo + 1;
  }
  return "";
});

function updateWrapperClass() {
  if (!rootRef.value || !rootRef.value) return; // Access the ref for the root element

  const wrapperEl = rootRef.value.closest(".sls-dynamic-link-wrapper");
  if (!wrapperEl) return;

  const hasIcon = isValid.value && !hasCustomText.value;
  const hasIconClass = CLASS.DYNAMIC_LINK_WRAPPER_HAS_ICON;
  const hasIconClassExist = wrapperEl.classList.contains(hasIconClass);

  if (hasIcon && !hasIconClassExist) {
    wrapperEl.classList.add(hasIconClass);
  }

  if (!hasIcon && hasIconClassExist) {
    wrapperEl.classList.remove(hasIconClass);
  }
}

function getDisplayLabel() {
  if (!isEmpty(customTextLocal.value)) return customTextLocal.value;
  if (dataLinkTypeLocal.value === LINK_TYPE.LESSON) return props.introductionTitle;
  if (dataLinkTypeLocal.value === LINK_TYPE.ACTIVITY) {
    return dynamicLinkStore.getAnchorActivity(anchorLocal.value)?.title;
  } else if (dataLinkTypeLocal.value === LINK_TYPE.PAGE) {
    // notebook page show page title, lesson page show activity title
    return (
      dynamicLinkStore.getAnchorNotebookPage(anchorLocal.value)?.title ??
      dynamicLinkStore.getActivityByAnchorPage(anchorLocal.value)?.title
    );
  }
  return defaultGenericText;
}

function handleClick($event: Event) {
  emit("click", {
    $event,
    type: props.dataLinkType,
    id:
      props.dataLinkType === LINK_TYPE.ACTIVITY
        ? dynamicLinkStore.getAnchorActivity(anchorLocal.value)?.id
        : (dynamicLinkStore.getAnchorPage(anchorLocal.value)?.id ??
          dynamicLinkStore.getAnchorNotebookPage(anchorLocal.value)?.id),
    displayLabel: getDisplayLabel(),
    clickLinkObject: {
      anchor: anchorLocal.value,
      customText: customTextLocal.value,
      dataLinkType: dataLinkTypeLocal.value,
    },
    rootElement: rootRef.value,
    handleEdit: handleEdit,
  });
}

function handleEdit({ anchor, customText, dataLinkType }) {
  anchorLocal.value = anchor;
  customTextLocal.value = customText;
  dataLinkTypeLocal.value = dataLinkType;
}

defineExpose({
  handleEdit,
});

// Watchers
watch(
  [isValid, hasCustomText],
  () => {
    // updateWrapperClass();
  },
  { immediate: true },
);

// Mounted lifecycle hook (equivalent of `mounted` in Options API)
onMounted(() => {
  updateWrapperClass();
  if (!isNotebookAnchor.value && isEmpty(dynamicLinkStore.state.lessonActivityAnchors)) {
    const lessonUuid = getLesson.value?.uuid ?? props.lessonUuid;
    if (lessonUuid) {
      dynamicLinkStore.fetchLessonAnchor(lessonUuid);
    }
  }
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.sls-dynamic-link {
  display: inline-block;
  cursor: pointer;
  .link {
    position: relative;
    display: flex;
    align-items: center;
    gap: $spacing-02;

    .icon-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      > .sls-icon {
        min-width: 32px;
      }

      + .label {
        font-weight: 700;
      }
    }

    .letter-icon-label {
      &::before {
        color: $ui-01;
      }
    }
    .number-icon-label {
      &::before {
        color: $icon-01;
      }
    }

    .letter-icon-label,
    .number-icon-label {
      position: absolute;
      width: 32px;
      text-align: center;
      &::before {
        @include carbon--type-style("helper-text-01");
        content: attr(data-icon-text);
      }
      &.hide-section-activity-number {
        &:not(.edit-mode)::before {
          opacity: 0;
        }
        &.edit-mode::before {
          opacity: 0.5;
        }
      }
    }

    .label {
      &::before {
        content: attr(data-text);
        color: $interactive-01;
      }
    }
  }
}
</style>
